import React, { useEffect, useRef } from "react";
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Fade,
  Grow,
  Slide,
  Divider,
  Button,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import ScrollToTop from "./ScrollToTop";
import SEO from "../components/Layout/SEO";

import { styled } from "@mui/material/styles";
import TeamSection from "../components/Leadership/TeamSection";
import {
  Psychology,
  GroupWork,
  WorkspacePremium,
  Diversity3,
  Lightbulb,
  School,
} from "@mui/icons-material";

const GradientBox = styled(Box)(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: "-50%",
    width: "200%",
    height: "100%",
    background: `linear-gradient(60deg, 
      ${theme.palette.primary.dark} 0%, 
      ${theme.palette.primary.main} 30%, 
      ${theme.palette.secondary.main} 70%, 
      ${theme.palette.secondary.dark} 100%)`,
    animation: "gradientAnimation 8s ease infinite",
  },
  "@keyframes gradientAnimation": {
    "0%": { backgroundPosition: "0% 50%" },
    "50%": { backgroundPosition: "100% 50%" },
    "100%": { backgroundPosition: "0% 50%" },
  },
}));

// Enhanced Paper component with animation
const AnimatedPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  height: "100%",
  display: "flex",
  flexDirection: "column",
  transition: "transform 0.3s, box-shadow 0.3s",
  "&:hover": {
    transform: "translateY(-8px)",
    boxShadow: theme.shadows[8],
  },
}));

// Custom icon wrapper
const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.primary.light,
  borderRadius: "50%",
  padding: theme.spacing(1.5),
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const ValueItem = ({ icon, title, description, delay }) => {
  return (
    <Grid item xs={12} sm={6} md={4}>
      <Grow
        in={true}
        style={{ transformOrigin: "0 0 0" }}
        timeout={1000}
        {...{ timeout: 1000 + delay * 300 }}
      >
        <AnimatedPaper elevation={3}>
          <IconWrapper>{icon}</IconWrapper>
          <Typography
            variant="h6"
            fontWeight="bold"
            gutterBottom
            color="primary.dark"
          >
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
        </AnimatedPaper>
      </Grow>
    </Grid>
  );
};

const LeadershipPage = () => {
  const theme = useTheme();
  const isLargeScreen = useMediaQuery(theme.breakpoints.up("md"));
  const missionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          entry.target.classList.add("visible");
        }
      },
      { threshold: 0.1 }
    );

    const currentRef = missionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <>
      <SEO
        title="Our Leadership Team"
        description="Meet the leadership team at ProtoDiag Solutions. Learn about our vision, mission, and the experts driving innovation in telecom communication infrastructure."
        keywords="ProtoDiag leadership, telecom company leadership, ProtoDiag team, communication infrastructure experts, telecom technology leaders"
        url="https://www.protodiag.com/leadership"
      />
      <ScrollToTop />

      {/* Animated Hero Section */}
      <GradientBox
        sx={{
          position: "relative",
          py: { xs: 8, md: 12 },
          color: "white",
          overflow: "hidden",
        }}
      >
        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Slide direction="down" in={true} timeout={800}>
            <Typography
              variant="h2"
              component="h1"
              fontWeight="bold"
              gutterBottom
            >
              Our Leadership
            </Typography>
          </Slide>
          <Slide direction="up" in={true} timeout={1000}>
            <Typography variant="h5" sx={{ maxWidth: 700, mb: 4 }}>
              Driven by pioneers in telecom innovation, our team is on a mission
              to redefine global connectivity through intelligent, secure, and
              scalable communication solutions.
            </Typography>
          </Slide>

          <Fade in={true} timeout={1500}>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                fontWeight: "bold",
                px: 4,
                py: 1.5,
                borderRadius: 2,
                boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
                "&:hover": {
                  boxShadow: "0 6px 20px rgba(0,0,0,0.3)",
                },
              }}
              onClick={() => {
                const teamSection = document.getElementById("team-section");
                if (teamSection) {
                  teamSection.scrollIntoView({ behavior: "smooth" });
                }
              }}
            >
              Meet Our Team
            </Button>
          </Fade>
        </Container>

        {/* Decorative elements */}
        {isLargeScreen && (
          <>
            <Box
              sx={{
                position: "absolute",
                bottom: -50,
                right: -50,
                width: 300,
                height: 300,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.1)",
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: -30,
                left: -30,
                width: 200,
                height: 200,
                borderRadius: "50%",
                backgroundColor: "rgba(255,255,255,0.05)",
                zIndex: 1,
              }}
            />
          </>
        )}
      </GradientBox>

      {/* Mission & Vision Section */}
      <Box sx={{ py: 8, bgcolor: "#f9f9f9" }} ref={missionRef}>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="overline"
                    component="div"
                    color="secondary"
                    fontWeight="bold"
                    sx={{ mb: 1 }}
                  >
                    OUR PURPOSE
                  </Typography>
                  <Typography
                    variant="h4"
                    component="h2"
                    fontWeight="bold"
                    color="primary.dark"
                    gutterBottom
                  >
                    Mission & Vision
                  </Typography>
                  <Divider
                    sx={{
                      width: 80,
                      borderWidth: 2,
                      borderColor: theme.palette.secondary.main,
                      mb: 3,
                    }}
                  />

                  <Typography variant="body1" paragraph sx={{ mb: 3 }}>
                    At ProtoDiag Solutions, we’re reimagining the future of
                    digital communication.
                  </Typography>

                  <Box
                    sx={{
                      p: 3,
                      bgcolor: "primary.light",
                      borderRadius: 2,
                      border: `1px solid ${theme.palette.primary.main}`,
                      mb: 3,
                    }}
                  >
                    <Typography
                      variant="h6"
                      color="primary.dark"
                      gutterBottom
                      fontWeight="medium"
                    >
                      Our Vision
                    </Typography>
                    <Typography variant="body2">
                      We envision a world where digital communication is
                      limitless—transcending barriers of scale, complexity, and
                      geography. ProtoDiag is committed to building intelligent,
                      interconnected ecosystems where businesses, telecom
                      operators, and users experience seamless and secure
                      communication. Our future-ready platforms are designed to
                      redefine how the world connects, engages, and transacts.
                    </Typography>
                  </Box>

                  <Typography variant="body1">
                    Our mission is to simplify and accelerate communication by
                    offering robust, carrier-grade platforms tailored for high
                    throughput, security, and compliance. Through real-time
                    APIs, intelligent routing engines, and AI-enhanced services,
                    we ensure that our partners stay ahead in delivering
                    mission-critical communication experiences globally.
                  </Typography>
                </Box>
              </Fade>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  position: "relative",
                  height: { xs: 300, md: 400 },
                  overflow: "hidden",
                  borderRadius: 4,
                  boxShadow: 4,
                  "& img": {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 8s",
                    "&:hover": {
                      transform: "scale(1.1)",
                    },
                  },
                }}
              >
                <img
                  src="/assets/images/meetingImage.jpg"
                  alt="ProtoDiag team collaborating"
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/600x400?text=ProtoDiag+Mission";
                  }}
                />
                <Box
                  sx={{
                    position: "absolute",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    p: 3,
                    background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
                    color: "white",
                  }}
                >
                  <Typography variant="h6" fontWeight="bold">
                    Transforming Lives Through Innovation
                  </Typography>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Team Members Section with ID for scroll target */}
      <Box id="team-section">
        <TeamSection />
      </Box>

      {/* Company Values Section */}
      <Box sx={{ py: 8, bgcolor: "white" }}>
        <Container>
          <Box sx={{ textAlign: "center", mb: 6 }}>
            <Typography
              variant="h4"
              component="h2"
              fontWeight="bold"
              gutterBottom
              color="primary.dark"
            >
              Our Core Values
            </Typography>
            <Divider
              sx={{
                width: 80,
                mx: "auto",
                borderWidth: 2,
                borderColor: theme.palette.secondary.main,
                mb: 3,
              }}
            />
            <Typography variant="body1" sx={{ maxWidth: 800, mx: "auto" }}>
              These values define our approach at ProtoDiag Solutions—from
              platform design to client engagement and global telecom
              partnerships.
            </Typography>
          </Box>

          <Grid container spacing={4} sx={{ mb: 6 }}>
            <ValueItem
              icon={<Lightbulb fontSize="large" />}
              title="Innovation"
              description="We constantly explore emerging telecom technologies to build secure, intelligent, and scalable communication platforms."
              delay={0}
            />

            <ValueItem
              icon={<GroupWork fontSize="large" />}
              title="Collaboration"
              description="We thrive on strong partnerships—with telecom operators, enterprises, and regulators—to enable seamless integration and mutual growth."
              delay={1}
            />

            <ValueItem
              icon={<WorkspacePremium fontSize="large" />}
              title="Excellence"
              description="We are committed to delivering carrier-grade platforms that meet the highest standards of reliability, performance, and compliance."
              delay={2}
            />

            <ValueItem
              icon={<Diversity3 fontSize="large" />}
              title="Integrity"
              description="We operate with transparency and adhere strictly to telecom regulations and data security protocols across every solution we offer."
              delay={3}
            />

            <ValueItem
              icon={<Psychology fontSize="large" />}
              title="Customer Focus"
              description="Our innovation is driven by real-world enterprise needs—helping clients achieve operational efficiency and superior user engagement."
              delay={4}
            />

            <ValueItem
              icon={<School fontSize="large" />}
              title="Continuous Improvement"
              description="We invest in ongoing learning and product evolution to stay ahead in a fast-changing digital communication landscape."
              delay={5}
            />
          </Grid>

          <Fade in={true} timeout={2000}>
            <Box
              sx={{
                bgcolor: "#1976d2", // softer than pure hot pink
                p: 4,
                borderRadius: 2,
                textAlign: "center",
                position: "relative",
                overflow: "hidden",
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundImage: "url('/assets/images/pattern-bg.png')",
                  opacity: 0.05,
                  zIndex: 0,
                }}
              />
              <Box sx={{ position: "relative", zIndex: 1 }}>
                <Typography
                  variant="h5"
                  sx={{ color: "#ffffff", fontWeight: "bold", mb: 2 }}
                >
                  Interested in Our Approach?
                </Typography>
                <Typography
                  variant="body1"
                  paragraph
                  sx={{ color: "#f5f5f5", mb: 3 }}
                >
                  Learn how ProtoDiag is shaping the future of telecom through
                  API-first platforms, AI-enhanced infrastructure, and
                  regulatory-aligned communication technologies.
                </Typography>
                <Button
                  variant="outlined"
                  size="large"
                  sx={{
                    fontWeight: "medium",
                    mt: 1,
                    borderRadius: 2,
                    px: 4,
                    color: "#ffffff",
                    borderColor: "#ffffff",
                    "&:hover": {
                      backgroundColor: "#ffffff",
                      color: "#d1005d",
                    },
                  }}
                  onClick={() => {
                    window.location.href = "/roadmap";
                  }}
                >
                  Our Approach
                </Button>
              </Box>
            </Box>
          </Fade>
        </Container>
      </Box>

      {/* Company Culture Section with parallax effect */}
      <Box
        sx={{
          py: { xs: 6, md: 12 },
          position: "relative",
          backgroundSize: "cover",
          backgroundAttachment: isLargeScreen ? "fixed" : "scroll",
          backgroundPosition: "center",
          color: "white",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "#1976d2 ",
            zIndex: 1,
          },
        }}
      >
        <Container sx={{ position: "relative", zIndex: 2 }}>
          <Grid container spacing={{ xs: 3, md: 6 }}>
            <Grid item xs={12} md={6}>
              <Typography
                variant="h4"
                component="h2"
                fontWeight="bold"
                gutterBottom
                sx={{ position: "relative", maxWidth: { xs: "100%", md: 450 } }}
              >
                Our Culture
                <Box
                  sx={{
                    position: "absolute",
                    bottom: -8,
                    left: 0,
                    width: 60,
                    height: 4,
                    bgcolor: theme.palette.secondary.main,
                  }}
                />
              </Typography>

              <Typography
                variant="body1"
                paragraph
                sx={{
                  mt: 4,
                  fontSize: { xs: "1rem", md: "1.1rem" },
                  maxWidth: { xs: "100%", md: 480 },
                }}
              >
                At ProtoDiag Solutions, we cultivate a culture of innovation,
                collaboration, and compliance-driven excellence. Our team is
                passionate about delivering carrier-grade communication
                platforms that empower enterprises and telecom operators
                globally.
              </Typography>

              <Typography
                variant="body1"
                paragraph
                sx={{ maxWidth: { xs: "100%", md: 480 } }}
              >
                We value diverse perspectives and encourage creative
                problem-solving to overcome complex telecom challenges. Every
                team member contributes to building scalable, secure, and
                AI-enhanced communication ecosystems that comply with regulatory
                standards and drive digital transformation.
              </Typography>

              <Typography
                variant="h6"
                sx={{ mt: 4, mb: 2, fontWeight: "medium" }}
              >
                Join Our Mission
              </Typography>

              <Typography
                variant="body1"
                paragraph
                sx={{ maxWidth: { xs: "100%", md: 480 } }}
              >
                We are always seeking talented, motivated individuals who want
                to help revolutionize telecom communication through
                next-generation technologies, intelligent APIs, and real-time
                data analytics.
              </Typography>

              <Button
                variant="outlined"
                color="secondary"
                size="large"
                sx={{
                  fontWeight: "medium",
                  mt: 1,
                  borderRadius: 2,
                  px: 4,
                  color: "#ffffff",
                  borderColor: "#ffffff",
                  "&:hover": {
                    backgroundColor: "#ffffff",
                    color: "#d1005d",
                  },
                }}
                onClick={() => {
                  window.location.href = "/contact";
                }}
              >
                View Career Opportunities
              </Button>
            </Grid>

            <Grid item xs={12} md={6}>
              <Grid container spacing={{ xs: 2, md: 3 }}>
                {[
                  {
                    title: "Collaborative Environment",
                    color: "primary.dark",
                    borderColor: theme.palette.primary.main,
                    text: `"At ProtoDiag, cross-functional teams work closely with telecom operators, enterprise clients, and regulatory bodies to design and deliver compliant, scalable communication solutions. Our collaborative approach fosters innovation and ensures seamless platform integration."`,
                    timeout: 1000,
                  },
                  {
                    title: "Learning & Development",
                    color: "secondary.dark",
                    borderColor: theme.palette.secondary.main,
                    text: `"We invest in ongoing training and knowledge-sharing to keep our team at the forefront of telecom technology, compliance regulations, and AI-powered communication innovations."`,
                    timeout: 1200,
                  },
                  {
                    title: "Work-Life Balance",
                    color: "primary.dark",
                    borderColor: theme.palette.primary.main,
                    text: `"We support flexible work models and wellness initiatives that foster creativity and sustainable productivity, empowering our team to innovate confidently while maintaining personal well-being."`,
                    timeout: 1400,
                  },
                ].map(({ title, color, borderColor, text, timeout }, index) => (
                  <Grid item xs={12} key={index}>
                    <Slide direction="left" in={true} timeout={timeout}>
                      <Paper
                        elevation={4}
                        sx={{
                          p: 3,
                          borderLeft: `4px solid ${borderColor}`,
                        }}
                      >
                        <Typography
                          variant="h6"
                          color={color}
                          gutterBottom
                          fontWeight="bold"
                        >
                          {title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {text}
                        </Typography>
                      </Paper>
                    </Slide>
                  </Grid>
                ))}
              </Grid>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Leadership Philosophy Section */}
      <Box sx={{ py: 8, bgcolor: "#f8f9fa" }}>
        <Container>
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: 4,
                  height: { xs: 300, md: 450 },
                  position: "relative",
                }}
              >
                <img
                  src="/assets/images/LeadershipImage.jpg"
                  alt="Leadership in action"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                  }}
                  onError={(e) => {
                    e.target.src =
                      "https://via.placeholder.com/600x450?text=Leadership+Philosophy";
                  }}
                />
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Fade in={true} timeout={1000}>
                <Box>
                  <Typography
                    variant="overline"
                    color="secondary"
                    fontWeight="bold"
                  >
                    OUR APPROACH
                  </Typography>
                  <Typography
                    variant="h4"
                    component="h2"
                    fontWeight="bold"
                    color="primary.dark"
                    gutterBottom
                  >
                    Leadership Philosophy
                  </Typography>
                  <Divider
                    sx={{
                      width: 80,
                      borderWidth: 2,
                      borderColor: theme.palette.secondary.main,
                      mb: 3,
                    }}
                  />

                  <Typography variant="body1" paragraph>
                    Our leadership philosophy is grounded in the principles that
                    power ProtoDiag’s mission to innovate and transform telecom
                    communication globally:
                  </Typography>

                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                  >
                    <Box
                      sx={{
                        borderRadius: "50%",
                        bgcolor: "primary.light",
                        color: "primary.main",
                        width: 40,
                        height: 40,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontWeight: "bold",
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      1
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        color="primary.dark"
                        gutterBottom
                      >
                        Visionary Innovation
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        We drive forward-looking telecom solutions, anticipating
                        future connectivity needs by harnessing cutting-edge
                        technologies such as AI, cloud-native architectures, and
                        real-time APIs to build scalable, secure communication
                        platforms.
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{ display: "flex", alignItems: "flex-start", mb: 2 }}
                  >
                    <Box
                      sx={{
                        borderRadius: "50%",
                        bgcolor: "primary.light",
                        color: "primary.main",
                        width: 40,
                        height: 40,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontWeight: "bold",
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      2
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        color="primary.dark"
                        gutterBottom
                      >
                        Empowered Collaboration
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        paragraph
                      >
                        Our leadership fosters a culture of collaboration across
                        telecom operators, enterprises, and regulatory bodies,
                        ensuring our solutions are compliant, interoperable, and
                        built with diverse expertise to solve complex
                        communication challenges.
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "flex-start" }}>
                    <Box
                      sx={{
                        borderRadius: "50%",
                        bgcolor: "primary.light",
                        color: "primary.main",
                        width: 40,
                        height: 40,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        fontWeight: "bold",
                        mr: 2,
                        flexShrink: 0,
                      }}
                    >
                      3
                    </Box>
                    <Box>
                      <Typography
                        variant="h6"
                        color="primary.dark"
                        gutterBottom
                      >
                        Purpose-Driven Execution
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        We focus relentlessly on delivering carrier-grade,
                        compliant communication platforms that drive measurable
                        business value, enhance user experience, and uphold the
                        highest standards of security and reliability.
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Fade>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </>
  );
};

export default LeadershipPage;
